import {
  useAllDisplays,
  usePatchStaticDisplayCreative,
  useGetStaticCreativeLineItemTrigger,
  usePatchStaticCreativeLineItem,
} from '@apis/displays';
import { GROUP_TYPE, useGroups } from '@apis/groups';
import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { pick } from 'lodash';
import { removeNullValues } from '../../utils';
import { bwSyncParams } from '@components/containers/WizardPage/constants';

import { fields } from '@components/CampaignWizard/AdGroupSettingsSection/formConfig';
import type { Campaign } from '@local-types/campaign';
import type { UseFormReturn } from 'react-hook-form';
import type { Fields } from '@local-types/general';
import { AdGroup } from '@local-types/group';
import { useGroupSectionPrefetch } from './useGroupSectionPrefetch';

export const useGroupSectionSubmitStaticAdGroup = ({
  campaign,
  form,
  onAfterSubmit,
  adGroup,
  disabled,
  shouldShowOutOfSyncSubmitPrompt,
}: {
  campaign: Campaign;
  disabled: boolean;
  onAfterSubmit: () => void;
  shouldShowOutOfSyncSubmitPrompt?: boolean;
  form: UseFormReturn<Fields<typeof fields>>;
  adGroup: AdGroup;
}) => {
  const { releaseCreativeStartEndDatetime: allowDateTime } = useLDFlags();
  const { prepareRequestData } = useGroupSectionPrefetch({
    adGroup,
    campaign,
    form,
    isDisplay: true,
  });

  const {
    trigger: updateLineItemStaticCreative,
    isMutating: isStaticCreativePostMutating,
  } = usePatchStaticCreativeLineItem();

  const { invalidate: invalidateDisplay } = useAllDisplays({
    disabled,
  });

  const {
    update: updateStaticDisplayAdGroup,
    create: createStaticDisplayAdGroup,
    isLoading: isStaticDisplayAdGroupProcessing,
  } = useGroups(GROUP_TYPE.STATIC_GROUP, campaign?.id);

  const { mutate: invalidateStaticAdGroupSync } = useGroups(
    GROUP_TYPE.STATIC_GROUP,
    campaign?.id,
    {
      ...(shouldShowOutOfSyncSubmitPrompt && { params: bwSyncParams }),
    }
  );

  const { trigger: getStaticCreativeLineItem } =
    useGetStaticCreativeLineItemTrigger();

  const { trigger: updateStaticDisplayCreative } =
    usePatchStaticDisplayCreative();

  const handleStaticDisplaySubmit = async (
    options: {
      updateAdGroupParams?: Record<string, string>;
    } = {}
  ) => {
    const { updateAdGroupParams } = options;
    const { creativesAdditionalData, ...rest } = prepareRequestData();

    const res = await (!adGroup?.temporary
      ? updateStaticDisplayAdGroup({
          id: adGroup.id,
          params: updateAdGroupParams,
          ...rest,
        })
      : createStaticDisplayAdGroup({ id: adGroup.id, ...rest }));

    /** TODO: move creatives hooks to typescript. */
    const lineItems = (
      allowDateTime
        ? /* @ts-expect-error js file doesn't recognize trigger params. */ await getStaticCreativeLineItem(
            res.id
          )
        : []
    ) as AdGroup[];
    const isCreativesDirty = form.formState.dirtyFields.creatives;

    if (res.id) {
      try {
        if (isCreativesDirty) {
          await Promise.all(
            res.creatives.map(async (staticDisplayCreativeId: number) => {
              const additionalData = creativesAdditionalData?.filter(
                ({ id }) => id === staticDisplayCreativeId
              )[0];

              await updateStaticDisplayCreative({
                ...removeNullValues({
                  ...additionalData,
                }),
                id: staticDisplayCreativeId,
              });

              const { start_date, end_date } = additionalData ?? {};
              const updatedData = pick(
                lineItems.find(
                  ({ creative }) => creative === staticDisplayCreativeId
                ),
                ['id', 'lineitem', 'creative']
              );

              if (allowDateTime) {
                await updateLineItemStaticCreative({
                  id: updatedData.id,
                  creative: staticDisplayCreativeId,
                  lineitem: updatedData.lineitem,
                  start_date,
                  end_date,
                });
              }
            })
          );

          invalidateDisplay();
        }
        if (shouldShowOutOfSyncSubmitPrompt) {
          invalidateStaticAdGroupSync();
        }

        onAfterSubmit();
      } catch (error) {
        console.error('Error updating static display creatives:', error);
      }
    }
  };

  return {
    submit: handleStaticDisplaySubmit,
    mutating:
      isStaticCreativePostMutating || isStaticDisplayAdGroupProcessing,
  };
};
